import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {Text, Button} from '@spothero/ui';
import IconCheck from '@spothero/icons/check';
import IconInfo from '@spothero/icons/info';
import AlertModal from 'common/alert-modal';
import dayjs from 'utils/dayjs-duration';

const CANCELLATION_NOT_ALLOWED = 'no';

const CancellationInfo = ({
    cancellationAllowed,
    showFreeCancellation,
    eventCancellationMinutes,
    canCancelHPEReservation,
}) => {
    const {starts} = useSelector(state => state.searchRequest);
    const [showModal, setShowModal] = useState(false);
    const toggleAlertModal = () =>
        setShowModal(prevShowModal => !prevShowModal);

    const cancellationCutOffTime = dayjs(starts).subtract(
        eventCancellationMinutes,
        'minute'
    );
    const canCancel =
        cancellationAllowed !== CANCELLATION_NOT_ALLOWED &&
        eventCancellationMinutes > 0
            ? canCancelHPEReservation
            : cancellationAllowed !== CANCELLATION_NOT_ALLOWED;

    if (canCancel && !showFreeCancellation) {
        return null;
    }

    if (eventCancellationMinutes > 0) {
        if (canCancel) {
            return (
                <Text fontSize="xs" lineHeight={1.3}>
                    Cancel until {dayjs(cancellationCutOffTime).format('MMM D')}{' '}
                    at {dayjs(cancellationCutOffTime).format('h:mm a')}
                </Text>
            );
        } else {
            return (
                <>
                    <Button
                        variant="tertiary"
                        onClick={toggleAlertModal}
                        rightIcon={<IconInfo height="10px" width="10px" />}
                        textTransform="auto"
                    >
                        <Text color="red.default" fontSize="xs">
                            This reservation is non-refundable
                        </Text>
                    </Button>
                    {showModal && (
                        <AlertModal
                            title="Cancellation"
                            message="Due to rules set by the operator, this reservation is not cancellable. If you choose to cancel this reservation, you will not be refunded any of your payment. You may, however, adjust this reservation to a different time up until 24 hours of the start time of the reservation."
                            onHidden={toggleAlertModal}
                        />
                    )}
                </>
            );
        }
    }

    if (canCancel) {
        return (
            <Text
                fontSize="xs"
                lineHeight={1.3}
                sx={{
                    '.Icon': {
                        position: 'relative',
                        top: '-1px',
                        height: 3,
                        width: 3,
                        marginRight: 1,
                        fill: 'green.default',
                    },
                }}
            >
                <IconCheck /> Free cancellation up to start time
            </Text>
        );
    }

    return (
        <>
            <Button
                variant="tertiary"
                onClick={toggleAlertModal}
                rightIcon={<IconInfo height="10px" width="10px" />}
                textTransform="auto"
            >
                <Text color="red.default" fontSize="xs">
                    This reservation is non-refundable
                </Text>
            </Button>
            {showModal && (
                <AlertModal
                    title="Cancellation"
                    message="Due to rules set by the operator, this reservation is not cancellable. If you choose to cancel this reservation, you will not be refunded any of your payment. You may, however, adjust this reservation to a different time up until 24 hours of the start time of the reservation."
                    onHidden={toggleAlertModal}
                />
            )}
        </>
    );
};

CancellationInfo.propTypes = {
    cancellationAllowed: PropTypes.string.isRequired,
    showFreeCancellation: PropTypes.bool,
    eventCancellationMinutes: PropTypes.number,
    canCancelHPEReservation: PropTypes.bool,
};

CancellationInfo.defaultProps = {
    showFreeCancellation: false,
    eventCancellationMinutes: 0,
    canCancelHPEReservation: false,
};

export default CancellationInfo;
